export const TONES = [0.16, 0.34, 0.6, 1] as const;

export const HOURS = 24;

export const DAYS = 7;

export const MOSAIC_CELL = 10;

export const MOSAIC_GAP = 2;

export const MOSAIC_RADIUS = 1.5;

export interface WeekCell {
  readonly day: number;
  readonly hour: number;
  readonly tone: number;
  readonly marked: boolean;
  readonly phase: number;
}

export interface WeekFlag {
  readonly day: number;
  readonly hour: number;
  readonly key: "stream" | "raid";
}

export const FLAGS: readonly WeekFlag[] = [
  { day: 3, hour: 17, key: "stream" },
  { day: 5, hour: 21, key: "raid" },
];

export const hash = (a: number, b: number): number => {
  let h = (a * 374761393 + b * 668265263) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
};

const bump = (at: number, centre: number, width: number): number => {
  const d = (at - centre) / width;
  return Math.exp(-d * d);
};

export const activity = (day: number, hour: number): number => {
  const evening = 0.82 * bump(hour, 20, 2.6);
  const lunch = 0.38 * bump(hour, 13, 2.1);
  const weekend = day >= 5 ? 0.34 * bump(hour, 15, 4.5) : 0;
  const level = 0.05 + evening + lunch + weekend + 0.16 * (hash(day, hour) - 0.5);
  return Math.min(1, Math.max(0, level));
};

const flagged = (day: number, hour: number): boolean =>
  FLAGS.some((flag) => flag.day === day && Math.abs(flag.hour - hour) <= 1);

export const toneStep = (level: number): number =>
  Math.min(TONES.length - 1, Math.max(0, Math.floor(level * TONES.length)));

export const quantise = (level: number): number => TONES[toneStep(level)]!;

export const NEWCOMER_WEEKS = [0.6, 0.63, 0.58, 0.62, 0.6, 0.64, 0.59, 0.61, 0.42] as const;
export const NEWCOMER_USUAL = { low: 0.56, high: 0.66, median: 0.61 } as const;

export const NEWCOMER_DRIVERS = [
  { by: "channel", points: -8 },
  { by: "source", points: -6 },
  { by: "role", points: -5 },
] as const;

export const week = (): readonly WeekCell[] => {
  const out: WeekCell[] = [];
  for (let day = 0; day < DAYS; day += 1) {
    for (let hour = 0; hour < HOURS; hour += 1) {
      const marked = flagged(day, hour);
      out.push({
        day,
        hour,
        tone: marked ? 1 : quantise(activity(day, hour)),
        marked,
        phase: hash(hour, day),
      });
    }
  }
  return out;
};
